import { createFileRoute } from '@tanstack/react-router'
import { TaskPageLayout } from '../components/TaskPageLayout'
import { CaughtUpEmptyState } from '../components/CaughtUpEmptyState'
import { DueGroupedTaskView } from '../components/DueGroupedTaskView'
import { useInboxTasks } from '../hooks/useInboxTasks'
import useViewModeStore from '@/store/ViewModeStore'
import { useTaskPageSetup } from '@/hooks'

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/tasks/',
)({
  component: TaskListPage,
})

/**
 * Inbox tasks page component that displays all unprocessed tasks.
 * 
 * This is the default task view that shows all tasks that haven't been
 * categorized into Today, Upcoming, or Someday. Acts as the main task
 * collection point for new and uncategorized tasks.
 * 
 * Features:
 * - Displays all inbox (uncategorized) tasks
 * - Full task management capabilities (create, edit, delete, reorder)
 * - Subtask navigation support
 * - Integrated with task page setup for consistent behavior
 * 
 * Route: `/$workspaceSlug/tasks/`
 * 
 * @example
 * ```tsx
 * // Accessed via URL like: /my-workspace/tasks/
 * // Renders the main inbox view with all uncategorized tasks
 * ```
 */
function TaskListPage() {
  useTaskPageSetup()

  const showSubTasks = useViewModeStore((s) => s.showSubTasks)
  const { tasks, isLoading } = useInboxTasks({ includeSubTasks: showSubTasks })
  const groupBy = useViewModeStore((s) => s.inboxGroupBy)

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <TaskPageLayout
        tasks={tasks}
        isLoading={isLoading}
        pageTitle="Inbox"
        renderEmptyState={(context) => <CaughtUpEmptyState view="inbox" {...context} />}
        renderTaskView={groupBy === 'dueDate' ? DueGroupedTaskView : undefined}
        navigateToSubtasks={true}
      />
    </div>
  )
}

export default TaskListPage
